"use client";
import React from "react";
import { Box, Typography, Container, Paper, Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Image from "next/image";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

export default function ComparisonSection() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const oldWayItemsRaw = t("comparisonSection.oldWayItems", {
    returnObjects: true,
  });
  const newWayItemsRaw = t("comparisonSection.newWayItems", {
    returnObjects: true,
  });
  const oldWayItems = Array.isArray(oldWayItemsRaw) ? oldWayItemsRaw : [];
  const newWayItems = Array.isArray(newWayItemsRaw) ? newWayItemsRaw : [];

  return (
    <Box
      sx={{
        backgroundImage: `url('${IMAGES.COMPARISON_SECTION_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        py: { xs: 6, md: 8 },
        px: { xs: 2, sm: 2, md: 2 },
        mt: { xs: "-19%", sm: "-19%", md: "1%" },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            mb: 6,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "transparent",
              px: 2,
              py: 1,
              borderRadius: "20px",
              mb: 2.3,
              border: "1px solid #9C9FAD",
            }}
          >
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                color: "#9C9FAD",
                fontWeight: 400,
                letterSpacing: "0.5px",
                fontFamily: "var(--font-outfit)",
              }}
            >
              {t("comparisonSection.badge")}
            </Typography>
          </Box>

          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: {
                xs: "1.5rem",
                sm: "1.75rem",
                md: "2.75rem",
                lg: "2.75rem",
                xl: "3.2rem",
              },
              fontWeight: "bold",
              lineHeight: 1.2,
              mb: 2.5,
              fontFamily: "var(--font-plusjakarta)",
            }}
          >
            {t("comparisonSection.heading1")}{" "}
            <Box component="span" sx={{ color: "#6002EB" }}>
              {t("comparisonSection.headingHighlight")}
            </Box>{" "}
            {t("comparisonSection.heading2")}
          </Typography>

          <Typography
            sx={{
              fontSize: {
                xs: "0.9rem",
                sm: "1rem",
                md: "1rem",
                lg: "1rem",
                xl: "1.3rem",
              },
              color: "#47405A",
              maxWidth: { xs: "100%", md: 860 },
              mx: { xs: 0, md: "auto" },
              lineHeight: 1.4,
              fontFamily: "var(--font-outfit)",
              fontWeight: "400",
            }}
          >
            {t("comparisonSection.subtitle")}
          </Typography>
        </Box>

        {/* Comparison Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            alignItems: "stretch",
            mt: "-1%",
          }}
        >
          {/* Old Way Card */}
          <Box sx={{ flex: 1 }}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: "#D2D2D2",
                borderRadius: { xs: "16px", md: "24px" },
                p: { xs: 2, sm: 2.5, md: 3 },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.7rem" },
                  fontWeight: "bold",
                  textAlign: "center",
                  mb: { xs: 2, md: 3 },
                  color: "#000000",
                  fontFamily: "var(--font-plusjakarta)",
                }}
              >
                {t("comparisonSection.oldWayTitle")}
              </Typography>

              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: { xs: "12px", md: "16px" },
                  p: { xs: 2, sm: 2.5, md: 3 },
                  minHeight: { xs: "auto", md: "290px" },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 1.5, md: 2 },
                    flex: 0,
                  }}
                >
                  {oldWayItems.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: isArabic ? "row" : "row",
                        gap: { xs: 1, md: 1.5 },
                      }}
                    >
                      <Image
                        src={IMAGES.WRONG_ICON}
                        alt="old way item"
                        width={24}
                        height={24}
                        style={{ flexShrink: 0 }}
                      />
                      <Typography
                        sx={{
                          fontSize: {
                            xs: "0.875rem",
                            sm: "0.95rem",
                            md: "1rem",
                          },
                          color: "#3D4C58",
                          lineHeight: 1.5,
                          fontFamily: "var(--font-outfit)",
                          fontWeight: 400,
                          textAlign: isArabic ? "right" : "left",
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Zayn Way Card */}
          <Box sx={{ flex: 1 }}>
            <Paper
              elevation={0}
              sx={{
                background: "linear-gradient(180deg, #9048FB 0%, #C096FF 100%)",
                borderRadius: { xs: "16px", md: "24px" },
                p: { xs: 2, sm: 2.5, md: 3 },
                display: "flex",
                flexDirection: "column",
                mb: { xs: 0, md: "8%" },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.7rem" },
                  fontWeight: "bold",
                  textAlign: "center",
                  mb: { xs: 2, md: 3 },
                  color: "#ffffff",
                  fontFamily: "var(--font-plusjakarta)",
                }}
              >
                {t("comparisonSection.newWayTitle")}
              </Typography>

              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: { xs: "12px", md: "16px" },
                  p: { xs: 2, sm: 2.5, md: 3 },
                  mb: 2,
                  minHeight: { xs: "auto", md: "290px" },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 1.5, md: 2 },
                    flex: 0,
                  }}
                >
                  {newWayItems.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: isArabic ? "row" : "row",
                        gap: { xs: 1, md: 1.5 },
                      }}
                    >
                      <Image
                        src={IMAGES.CORRECT_ICON}
                        alt="zayn way item"
                        width={24}
                        height={24}
                        style={{ flexShrink: 0 }}
                      />
                      <Typography
                        sx={{
                          fontSize: {
                            xs: "0.875rem",
                            sm: "0.95rem",
                            md: "1rem",
                          },
                          color: "#231A30",
                          lineHeight: 1.5,
                          fontFamily: "var(--font-outfit)",
                          fontWeight: 500,
                          textAlign: isArabic ? "right" : "left",
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* CTA Button */}
              <Button
                variant="contained"
                endIcon={
                  <ArrowForwardIcon
                    sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
                  />
                }
                onClick={() => {
                  const section = document.getElementById("talkToZaynSection");
                  if (section) {
                    section.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }}
                sx={{
                  backgroundColor: "#000000",
                  color: "#ffffff",
                  py: { xs: 1.2, md: 1.5 },
                  borderRadius: { xs: "10px", md: "12px" },
                  textTransform: "none",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  fontWeight: 500,
                  fontFamily: "var(--font-outfit)",
                  boxShadow: "none",
                  transition: "transform 0.2s ease-in-out",
                  "& .MuiButton-endIcon": {
                    marginLeft: isArabic ? "16px" : "8px",
                    marginRight: isArabic ? "-4px" : "-4px",
                    display: "flex",
                    alignItems: "center",
                  },
                  "&:hover": {
                    backgroundColor: "#775CE5",
                    boxShadow: "none",
                    transform: "scale(1.05) translateY(-2px)",
                  },
                }}
              >
                {t("comparisonSection.cta")}
              </Button>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
